import {
  SelectionState,
  SelectionStatus,
} from "@monolith-forensics/monolith-ui";

import { hasDataArray } from "@/api/connection.types";

import { EvidenceAPI } from "../evidence.api";
import type {
  GetEvidenceQuery,
  GetEvidenceResponse,
} from "../evidence.api.types";
import type { Evidence } from "../evidence.types";

/*
  Turns the evidence table's selection into the records a label run prints.
  The case, client and org data a template needs is loaded separately, in
  Features/Labels/evidenceLabelLookups.ts. Label printing
  runs in the browser, so this is the client side twin of the API's
  resolveEvidenceRecordsForBulkAction (evidence-bulk-coc.service.ts), with two
  deliberate differences:

  - Selected rows are always refetched by uuid, never taken from the table's
    loaded rows. Nested `children` rows are a trimmed object without
    `intake_date`, `received_by` and `received_from`, so a ticked child taken
    from the table would print those fields blank.
  - Exclusions are dropped in the browser and the table's query is sent as is.
    The API adds a `uuid nin` rule and forces `combinator: "and"`, which breaks
    OR filters (ENG-2076).

  The app's API layer never rejects: a failed request resolves as
  `{ success: false, error }`, and a successful read has no `success: true`.
  So every page is checked on its payload, never on a positive flag.
*/

/** Page size for resolving a selection, the same as the API's bulk actions. */
const RESOLVE_PAGE_SIZE = 1000;

/**
 * Resolving skips the global error toast: a failed run shows its own error in
 * the print dialog.
 */
const SKIP_TOAST = { skipErrorToast: true };

export type ResolveSelectedEvidenceArgs = {
  selectionState: SelectionState;
  /** The table's own query: filter, combinator, sort, search, include_children. */
  resolvedQuery: GetEvidenceQuery;
  /** The table's rows in display order (depth first `orderedRecords`). */
  loadedRecords: Evidence[];
};

/**
 * Pages through `POST /evidence/query` until `nextPage` runs out. Throws on any
 * page that isn't `{ data: Array }`, so a failed request never reads as an
 * empty selection.
 */
const queryAllPages = async (query: GetEvidenceQuery): Promise<Evidence[]> => {
  const rows: Evidence[] = [];
  let page: number | null = 1;

  while (page) {
    const res: GetEvidenceResponse = await EvidenceAPI.query(
      {
        ...query,
        pageSize: RESOLVE_PAGE_SIZE,
        page,
      },
      SKIP_TOAST
    );

    if (!hasDataArray(res)) {
      throw new Error(
        `Couldn't load the selected evidence items (page ${page} failed).`
      );
    }

    rows.push(...res.data);
    page = res.nextPage ?? null;
  }

  return rows;
};

/**
 * The records a label run prints for the table's current selection, in table
 * order. Exactly the selected rows: nested `children` are never flattened into
 * the result.
 */
export const resolveSelectedEvidence = async ({
  selectionState,
  resolvedQuery,
  loadedRecords,
}: ResolveSelectedEvidenceArgs): Promise<Evidence[]> => {
  switch (selectionState?.selectionStatus) {
    case SelectionStatus.SomeIncluded: {
      const keys = selectionState.selectedRowKeys || [];

      // The API drops an empty IN rule and would return every item.
      if (keys.length === 0) return [];

      // No case scope and no include_children: a ticked child comes back as a
      // full top level row.
      const rows = await queryAllPages({
        filter: {
          rules: [
            {
              label: "uuid",
              dataField: "uuid",
              operator: { label: "is any of", value: "in" },
              value: keys,
            },
          ],
          combinator: "and",
        },
      });

      // Table order for the rows the table has loaded. Keys it hasn't loaded go
      // last, in the order the server returned them (sort is stable).
      const position = new Map<string, number>();
      loadedRecords.forEach((record, index) => {
        if (!position.has(record.uuid)) position.set(record.uuid, index);
      });
      const rank = (record: Evidence) =>
        position.get(record.uuid) ?? Number.MAX_SAFE_INTEGER;

      return [...rows].sort((a, b) => rank(a) - rank(b));
    }

    case SelectionStatus.All:
      return queryAllPages(resolvedQuery);

    case SelectionStatus.SomeExcluded: {
      const excluded = new Set(selectionState.excludedRowKeys || []);
      const rows = await queryAllPages(resolvedQuery);
      return rows.filter((record) => !excluded.has(record.uuid));
    }

    default:
      return [];
  }
};
